import { Flex, Heading, HStack, IconButton, Stack, Table, Tbody, Td, Text, Tr } from '@chakra-ui/react';
import { TenantId } from '@pelicargo/tenants';
import { useError } from '@pelicargo/ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { HiRefresh, HiSave, HiTrash } from 'react-icons/hi';

import { SimpleForm } from '../components/SimpleForm';
import { SimpleInput } from '../components/SimpleInput';
import { SimpleSelect } from '../components/SimpleSelect';
import { TypesenseSelect } from '../components/TypesenseSelect';
import { trpc, TrpcOutputs } from '../config/trpc';
import { useAuth } from '../context/AuthProvider';

const CredentialEditor = ({
  data,
  account_id,
  tenant_id,
  refetch,
}: {
  data: { key: TrpcOutputs['getCredentialRegistry'][number]['key']; value?: boolean; description: string };
  account_id: number;
  tenant_id: TenantId;
  refetch: () => any;
}) => {
  const { handleError } = useError();
  const { mutateAsync } = trpc.upsertCredential.useMutation();
  const { mutateAsync: deleteAsync } = trpc.deleteCredential.useMutation();

  const methods = useForm({
    defaultValues: {
      value: data['value'] ? '*******' : '',
    },
  });

  const reset = useCallback(() => {
    methods.reset({
      value: data['value'] ? '*******' : '',
    });
  }, [data, methods]);

  useEffect(() => {
    reset();
  }, [data, methods, reset]);

  const dirty = methods.getFieldState('value').isDirty;
  const handleSubmit = methods.handleSubmit(async ({ value }) => {
    try {
      await mutateAsync({
        value,
        key: data.key,
        account_id,
        tenant_id,
      });
      await refetch();
    } catch (e) {
      handleError(e);
    }
  });
  const handleDelete = async () => {
    try {
      await deleteAsync({
        account_id,
        tenant_id,
        key: data.key,
      });
      await refetch();
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <SimpleForm {...methods}>
      <Tr
        _odd={{
          background: 'blackAlpha.50',
        }}
      >
        <Td>{data.key}</Td>
        <Td>{data.description}</Td>
        <Td>
          <SimpleInput name="value" isHighlighted={dirty} helper={dirty ? 'Edited!' : ''} />
        </Td>
        <Td>
          <Flex gap={2}>
            <IconButton
              aria-label="Save"
              icon={<HiSave />}
              onClick={handleSubmit}
              colorScheme="green"
              isDisabled={!dirty}
            />
            <IconButton
              aria-label="Reset"
              onClick={reset}
              icon={<HiRefresh />}
              colorScheme="yellow"
              isDisabled={!dirty}
            />
            <IconButton aria-label="Delete" onClick={handleDelete} icon={<HiTrash />} colorScheme="red" />
          </Flex>
        </Td>
      </Tr>
    </SimpleForm>
  );
};

export const CredentialScreen = () => {
  const { data: registeredCredentials, refetch } = trpc.getAllCredentials.useQuery();
  const { data: registry } = trpc.getCredentialRegistry.useQuery();
  const { knownTenants } = useAuth();

  const filterMethods = useForm<{ account_id: number; tenant_id: TenantId }>({
    defaultValues: {
      account_id: null,
      tenant_id: 'pelicargo',
    },
  });

  const selectedAccount = useWatch({
    control: filterMethods.control,
    name: 'account_id',
  });

  const selectedTenant = useWatch({
    control: filterMethods.control,
    name: 'tenant_id',
  });

  // ugly, but just merges registered credentials with the registry so that all fields are available, and ones with values have ***
  const filtered = useMemo(() => {
    const reduced = registeredCredentials
      ?.filter((cred) => cred.tenant_id === (selectedTenant || null) && cred.account_id == (selectedAccount || null))
      ?.reduce(
        (agg, cred) => ({ ...agg, [cred.key]: cred }),
        {} as Record<string, (typeof registeredCredentials)[number]>,
      );
    return registry?.map((x) => ({ ...x, ...(reduced?.[x.key] ? reduced?.[x.key] : {}) })) ?? [];
  }, [registeredCredentials, registry, selectedAccount, selectedTenant]);

  return (
    <Stack spacing="2" maxH="100vh" h="100vh" p="4" overflowY="scroll">
      <Stack borderBottom="1px solid" borderColor="blackAlpha.500" pb="2">
        <Heading variant="h5">Credentials</Heading>
        <Text>
          *Extremely* sharp knives. Credentials can be set, but not displayed. Please triple-check additions and edits
          to make sure you're on the right combination of accounts and tenants.
        </Text>
        <HStack>
          <Text w="40">Tenant/Account:</Text>
          <SimpleForm {...filterMethods}>
            <SimpleSelect
              w={40}
              name="tenant_id"
              options={knownTenants.map((k) => ({
                label: k,
                value: k,
              }))}
            />
            <Flex w={40} justifyItems="stretch">
              <TypesenseSelect
                onChange={({ value }) => {
                  filterMethods.setValue('account_id', value);
                }}
                name="account_id"
                shouldProcessValue
                collection="accounts"
                queryBy="name"
                documentLabel="name"
                documentValue="account_id"
                suffixWithValue
                hasBlank
              />
            </Flex>
          </SimpleForm>
        </HStack>
      </Stack>
      <Table>
        <Tbody>
          {filtered.map((f) => (
            <CredentialEditor
              key={f.key}
              data={f}
              account_id={selectedAccount}
              tenant_id={selectedTenant}
              refetch={refetch}
            />
          ))}
        </Tbody>
      </Table>
    </Stack>
  );
};
